import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import { recordId, relatedRows } from "@/profitability/records/objects";
import { RecordRelated } from "@/profitability/records/record-related";
import { SectionHeading } from "@/profitability/records/section-heading";
import { FindingNote } from "@/profitability/review/finding-note";
import { GroupedActivityFeed } from "@/profitability/review/grouped-activity";
import { RecordDetails } from "./record-details";
import { RecordFinding } from "./record-finding";
import { RecordHighlights } from "./record-highlights";
import { RecordHistorySection } from "./record-history";
import { type RecordPeriod, RecordMissing } from "./record-missing";
import type { RecordData } from "./use-record";
import { useRecordGaps } from "./use-record-gaps";

const RELATED = 5;

/**
 * A record in one column, no tabs: what was flagged, its figures, the
 * groups of fields its script declares (or every field when asked), how it
 * moved period to period, the records that link to it in the script's
 * order, and its timeline with a comment box on top. Sections are split by
 * a hairline and named by small headings, so the structure does the work a
 * box would.
 */
export const RecordSections = ({
  businessId,
  reportId,
  data,
  objects,
  onOpenRecord,
  period = null,
  canUpload = false,
  onAsk,
  onShowSource,
  showFlag = true,
}: {
  businessId: string;
  reportId: string;
  data: RecordData & {
    object: RecordObject;
    row: NonNullable<RecordData["row"]>;
  };
  objects: RecordObject[];
  onOpenRecord: (ref: RecordRef) => void;
  /** The period the row is from, where its missing figures are filled. */
  period?: RecordPeriod | null;
  canUpload?: boolean;
  onAsk?: (question: string) => void;
  /** Opens the file a figure (a column's key) was read from. */
  onShowSource?: (column?: string | null) => void;
  /**
   * The finding's banner, note, and timeline. Off when the page already
   * shows them and this column is the record beside it.
   */
  showFlag?: boolean;
}) => {
  const [details, setDetails] = useState(false);
  const { object, row, finding, activity, name } = data;
  // A linked record names this one by its key or id, or by its name in a
  // report that links by name; never only by the name it shows.
  const linked = (other: RecordObject) =>
    other.dataset.id !== object.dataset.id &&
    relatedRows(other, object, row).length > 0;
  // The script names which linked records to list, in its order; else all.
  const declaredRelated = object.detail?.related;
  const related = declaredRelated
    ? declaredRelated.flatMap((id) =>
        objects.filter((other) => other.dataset.id === id && linked(other)),
      )
    : objects.filter(linked);
  const groups = object.detail?.groups ?? [];
  // Which missing figures are gaps of their own, and which wait on them.
  const { gaps, pending } = useRecordGaps(
    businessId,
    period?.runId,
    object,
    row,
  );
  return (
    <div className="flex flex-col gap-6 divide-y divide-line px-5 pb-6 *:pb-6 *:last:pb-0">
      {showFlag && finding && (
        <RecordFinding
          businessId={businessId}
          finding={finding}
          label={`${object.name.toLowerCase()} ${name}`}
          noun={object.name.toLowerCase()}
        />
      )}
      <RecordMissing
        businessId={businessId}
        reportId={reportId}
        object={object}
        row={row}
        period={period}
        gaps={gaps}
        pending={pending}
        canUpload={canUpload}
        onAsk={onAsk}
        onShowSource={onShowSource}
      />
      <section aria-label="Figures" className="flex flex-col gap-3">
        <RecordHighlights
          object={object}
          objects={objects}
          row={row}
          gaps={gaps}
          onOpenRecord={onOpenRecord}
          onShowSource={onShowSource}
        />
        {groups.length === 0 && (
          <Button
            variant="link"
            size="sm"
            className="h-auto self-start p-0"
            aria-expanded={details}
            onClick={() => setDetails(!details)}
          >
            {details ? "Hide fields" : "All fields"}
          </Button>
        )}
        {details && groups.length === 0 && (
          <RecordDetails
            object={object}
            objects={objects}
            row={row}
            gaps={gaps}
            onOpenRecord={onOpenRecord}
            onShowSource={onShowSource}
          />
        )}
      </section>
      {groups.map((group) => (
        <section
          key={group.title}
          aria-label={group.title}
          className="flex flex-col gap-2"
        >
          <SectionHeading>{group.title}</SectionHeading>
          <RecordDetails
            object={object}
            objects={objects}
            row={row}
            only={group.fields}
            gaps={gaps}
            onOpenRecord={onOpenRecord}
            onShowSource={onShowSource}
          />
        </section>
      ))}
      <RecordHistorySection
        businessId={businessId}
        reportId={reportId}
        object={object}
        recordId={recordId(object.dataset, row)}
        name={name}
        periods={data.history}
      />
      {related.map((other) => (
        <RecordRelated
          key={other.dataset.id}
          object={other}
          target={object}
          targetRow={row}
          onOpenRecord={onOpenRecord}
          limit={RELATED}
        />
      ))}
      {showFlag && finding && (
        <section aria-label="Timeline" className="flex flex-col gap-3">
          <SectionHeading>Timeline</SectionHeading>
          <FindingNote businessId={businessId} findingId={finding.id} />
          <GroupedActivityFeed
            activity={activity}
            hypothesis={finding.hypothesis}
          />
        </section>
      )}
    </div>
  );
};
